import React from "react";
import { Clock, DollarSign, Layers, LogOut, Settings } from "react-feather";
import { Link } from "react-router-dom";
import { styled } from "styled-components";
import logo from "../../assets/images/logo.png";
import { NavLink } from "react-router-dom";
import { useQueryClient } from "react-query";
import ApiService from "../../services/ApiService";
import { clearLaunchCreds } from "../../services/launchCreds";
import {
  buildBridgeMessage,
  postMessageBridge,
} from "../../services/extensionBridge";
import Button from "@cs/components/src/Button";
import StagingBadge from "@cs/components/src/StagingBadge";

const EXTENSION_ID = process.env.REACT_APP_ICONIK_EXTENSION_ID;
const chromeRuntime = () => (window as any).chrome?.runtime;

interface Props {
  showLinks: boolean;
  systemDomainId: string | undefined;
}

const Spacer = styled.div`
  flex: 1;
`;

const StyledHeader = styled.div`
  /* One clear step lighter than the page canvas (index.css's body, --dark060/nflPaper) so the
     header reads as a distinct bar instead of blending into the page — --dark090 is Spotter's own
     header-bar tone (nflSlate), a separate token from --dark100's card-surface role (nflSurface). */
  background-color: var(--dark090);
  height: 50px;
  color: var(--gray200);
  display: flex;
  align-items: center;
  padding: 0 15px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.39);

  > a {
    flex-shrink: 0;
  }
`;

const Logo = styled.img`
  height: 30px;
  margin-top: 5px;
  margin-right: 10px;
`;

const HeaderLink = styled(NavLink)`
  margin-left: 15px;
  display: flex;
  align-items: center;
  text-transform: uppercase;
  font-size: 13px;
  font-weight: 500;

  svg {
    margin-right: 5px;
  }

  span {
    display: none;
  }

  &.active {
    color: var(--blue400);
  }

  @media (min-width: 600px) {
    margin-left: 25px;

    span {
      display: block;
    }
  }
`;

const Header = (props: Props) => {
  const { showLinks, systemDomainId } = props;
  const queryClient = useQueryClient();

  const signOut = async () => {
    // Notify the extension to revoke iconik tokens BEFORE clearing local state.
    // The extension reads token/appId from the open iconik tab's localStorage,
    // sends DELETE /API/auth/v1/auth/token/ WITH those creds, then clears them.
    // If the extension is not installed or the message fails, we proceed anyway.
    const logoutMsg = buildBridgeMessage("ICONIK_LOGOUT");
    const rt = chromeRuntime();
    if (EXTENSION_ID && rt) {
      rt.sendMessage(EXTENSION_ID, logoutMsg);
    } else if (!rt) {
      // Safari path — relay via content-script postMessage bridge.
      postMessageBridge(logoutMsg);
    }

    // Tell the backend to clear the session cookie and invalidate the DynamoDB row —
    // a client-only "forget the cookie" isn't possible anyway since it's httpOnly. Wait for
    // it (best-effort — swallow failure) before invalidating below: otherwise the ["session"]
    // refetch can race the DELETE and re-read the still-valid cookie, leaving the UI signed in.
    await ApiService.fetch("DELETE", "/api/session").catch(() => {});

    clearLaunchCreds();
    queryClient.invalidateQueries(["session"]);
  };

  // const location = useLocation();

  // let path = 'plugin';
  // // if (location.pathname.includes('/plugin/')) {
  // //   path = location.pathname.replace(/^.*\/(plugin\/[^/]*).*$/, '$1');
  // // }
  // if (location.pathname.includes('/script')) {
  //   path = 'script';
  // }
  // if (location.pathname.includes('/script/') && !location.pathname.includes('/job')) {
  //   path = location.pathname.replace(/^.*\/(script\/[^/]*).*$/, '$1');
  // }

  return (
    <StyledHeader>
      <Link to="/dashboard">
        <Logo src={logo} alt="cinesys.io" />
      </Link>
      <StagingBadge />
      {showLinks && (
        <>
          <HeaderLink
            to={`/dashboard${
              systemDomainId ? `/${systemDomainId}/plugin` : ""
            }`}
          >
            <Layers size={23} />
            <span>Dashboard</span>
          </HeaderLink>
          <HeaderLink to={`/job/job`}>
            <Clock size={23} />
            <span>Jobs</span>
          </HeaderLink>
          <HeaderLink to={`/config/organization`}>
            <Settings size={23} />
            <span>Config</span>
          </HeaderLink>
          <HeaderLink to={`/billing`}>
            <DollarSign size={23} />
            <span>Billing</span>
          </HeaderLink>
        </>
      )}
      <Spacer />
      {showLinks && (
        <Button
          icon={LogOut}
          label="Sign out"
          onClick={signOut}
          borderless
          type="default"
        />
      )}
    </StyledHeader>
  );
};

export default Header;
